import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "../components/sub-comp/Navbar.jsx";
import Footer from "../components/sub-comp/MainFooter.jsx";
import FloatingSocialPanels from "../components/sub-comp/FloatingSocialPanels";
import CourseHeader from "../components/Courses/Shared/CourseHeader";
import ciscoImg from "../assets/cisco.png";
import cmsApi from "../services/cmsApi";
import { getImageUrl } from "../utils/getImageUrl";
import { getCareerPaths, findCareerPath, LANGUAGE_LABELS, capitalize } from "../utils/ciscoCareerPaths";

// Groups a path's courses by their `level` field (e.g. multiple language
// variants under "beginner" for the Programming path) into one outline row
// per level. Courses without a `level` (every other career path) each become
// their own single-item group, keeping today's one-row-per-course behavior
// and links unchanged.
function groupCoursesByLevel(courses) {
  const order = [];
  const groups = new Map();
  courses.forEach((course) => {
    const key = course.level || course.slug;
    if (!groups.has(key)) {
      order.push(key);
      groups.set(key, []);
    }
    groups.get(key).push(course);
  });
  return order.map((key) => {
    const group = groups.get(key);
    const languages = [...new Set(group.map((c) => c.language).filter(Boolean))];
    return {
      key,
      name: group.length > 1 ? capitalize(key) : group[0].name,
      description:
        group.length > 1
          ? languages.map((l) => LANGUAGE_LABELS[l] || l).join(" · ")
          : group[0].description,
      image: group[0].image,
    };
  });
}

// Career path page, e.g. /courses/professional-courses/cisco-networking-academy/it-support-specialist
// Hero + a vertical "Career Path — Outline" list of the courses within this path.
// Clicking a course goes to its own full detail page (CiscoCourseDetailPage).
const CiscoCareerPathPage = () => {
  const { pathSlug } = useParams();
  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    cmsApi
      .getProgramBySlug("cisco-networking-academy")
      .then((res) => {
        if (res.success && res.program) setProgram(res.program);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [pathSlug]);

  const paths = getCareerPaths(program?.ciscoCertTracksSection ?? null);
  const path = findCareerPath(paths, pathSlug);

  if (loading) {
    return (
      <div>
        <Navbar />
        <div className="flex justify-center items-center py-32">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-custom-pink" />
        </div>
        <Footer />
      </div>
    );
  }

  if (!path) {
    return (
      <div>
        <Navbar />
        <div className="mid-desktop-page container mx-auto px-4 sm:px-6 lg:px-8 max-w-3xl py-24 text-center">
          <h1 className="text-3xl font-bold text-custom-pink mb-4">Career path not found</h1>
          <p className="text-gray-600 mb-8">
            We couldn&apos;t find that career path. It may have been renamed or removed.
          </p>
          <Link
            to="/courses/professional-courses/cisco-networking-academy"
            className="inline-block bg-custom-pink text-white font-bold px-6 py-3 rounded-lg hover:opacity-90 transition-opacity"
          >
            &larr; Back to Cisco Networking Academy
          </Link>
        </div>
        <Footer />
        <FloatingSocialPanels />
      </div>
    );
  }

  return (
    <div>
      <Navbar />
      <div className="mid-desktop-page">
        <CourseHeader
          title={path.name}
          highlight="Career Path"
          subtitle={path.tag}
          backgroundImage={(path.heroImage || program?.heroImage) ? getImageUrl(path.heroImage || program.heroImage) : ciscoImg}
          cta={{ label: "Get Started", targetId: "career-path-outline" }}
          breadcrumbs={[
            { label: "Home", href: "/" },
            { label: "Professional Courses", href: "/course/professional-courses" },
            { label: "Cisco Networking Academy", href: "/courses/professional-courses/cisco-networking-academy" },
            { label: `${path.name} Career Path` },
          ]}
        />

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl py-16 md:py-24">
          <Link
            to="/courses/professional-courses/cisco-networking-academy"
            className="mb-8 inline-flex items-center gap-2 text-custom-pink font-semibold hover:underline"
          >
            <span aria-hidden="true">&larr;</span> Back to Cisco Networking Academy
          </Link>

          <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-14">{path.heroIntro}</p>

          <h2 id="career-path-outline" className="text-2xl md:text-3xl font-bold text-gray-900 mb-8 scroll-mt-28">
            Career Path — Outline
          </h2>

          <div className="relative">
            <div className="absolute left-5 top-10 bottom-10 w-px bg-custom-pink/25" aria-hidden="true" />
            <div className="space-y-6">
              {groupCoursesByLevel(path.courses).map((row) => (
                <div key={row.key} className="relative flex gap-6">
                  <div className="relative z-10 flex-shrink-0 w-10 h-10 rounded-full border-2 border-custom-pink bg-white flex items-center justify-center">
                    <svg className="w-4 h-4 text-custom-pink" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                  </div>
                  <Link
                    to={`/courses/professional-courses/cisco-networking-academy/${path.slug}/${row.key}`}
                    className="flex-1 flex flex-col sm:flex-row gap-5 bg-white border border-gray-200 rounded-2xl p-5 hover:shadow-md hover:border-custom-pink/30 transition-all duration-300"
                  >
                    {row.image ? (
                      <img
                        src={getImageUrl(row.image)}
                        alt={row.name}
                        className="w-full sm:w-40 h-28 object-cover rounded-xl flex-shrink-0"
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                      />
                    ) : (
                      <div className="w-full sm:w-40 h-28 rounded-xl bg-gradient-to-br from-custom-pink/10 to-gold/10 flex-shrink-0" />
                    )}
                    <div>
                      <span className="text-xs font-semibold text-custom-pink uppercase tracking-wide">Course</span>
                      <h3 className="text-lg font-bold text-gray-900 mt-1 mb-1">{row.name}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{row.description}</p>
                    </div>
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <Footer />
      <FloatingSocialPanels />
    </div>
  );
};

export default CiscoCareerPathPage;
